import React, { useState, useEffect, useRef } from 'react';
import { Tradesperson } from '@/lib/types';
import { getTradeDisplayName, getTradeIconForTrade, getTradeType } from '@/lib/tradeUtils';
import { ContactActionButton } from '@/components/ContactActionButton';
import { TradeIcon } from '@/components/ui/trade-icon';
import { Phone, MessageSquare, MessageSquareShare, MoreVertical, Check, Copy, Mail, Share2, BookmarkPlus, Trash2, Edit, Save, X, Users, Sparkles, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { addUserTradesperson, isTradesPersonSavedByUser, updateUserTradesperson } from '@/lib/supabase/trades';
import { debug } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { triggerHaptic, hapticPatterns } from '@/lib/haptic';
import { BadgeInfo } from '@/lib/badgeUtils';
import { formatPostcodeDisplay } from '@/lib/postcodeUtils';
import { generateMessage, QuickContactTemplateData } from '@/lib/messaging/templates';
import { generateWhatsAppUrl } from '@/lib/messaging/whatsapp';
import { useProfile } from '@/hooks/useProfile';
import { useIsMobile } from '@/hooks/use-mobile';

// Type definitions
interface TradePersonCardProps {
  trade: Tradesperson & { badge_info?: BadgeInfo };
  mode: 'saved' | 'public'; // Whether this is on the saved page or public results
  username?: string; // Current user's username for sharing
  postcode?: string; // Optional postcode for context in messages
  onSave?: () => void; // Optional callback for when a trade is saved
  onRemove?: (tradeId: string) => void; // Optional callback for removing a trade
  onUndoRemove?: (tradeId: string) => void; // Optional callback for undoing a removal
  notes?: string; // Optional notes from the user-tradesperson relationship
  relationId?: string; // Optional relationship ID for updating notes
  isExpanded?: boolean; // Whether this card is currently expanded
  onToggleExpanded?: (tradeId: string) => void; // Callback for toggling expanded state
}

export function TradePersonCard({
  trade,
  mode,
  username,
  postcode,
  onSave,
  onRemove,
  onUndoRemove,
  notes: initialNotes = '',
  relationId,
  isExpanded = false,
  onToggleExpanded
}: TradePersonCardProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isCheckingSaved, setIsCheckingSaved] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);
  const [isRemoved, setIsRemoved] = useState<boolean>(false);
  const [isEditingNotes, setIsEditingNotes] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>(initialNotes || '');
  const [expandedNotes, setExpandedNotes] = useState<boolean>(false);
  const { profile: userProfile } = useProfile();

  const notesRef = useRef<HTMLTextAreaElement>(null);

  const displayName = getTradeDisplayName(trade);
  const tradeType = getTradeType(trade);
  // Get properly capitalized display name for the trade type
  const tradeTypeDisplay = tradeType ? tradeType.charAt(0).toUpperCase() + tradeType.slice(1) : '';

  // Check if this tradesperson is already saved by the user
  useEffect(() => {
    if (!user || !trade.id) return;

    const checkIfSaved = async () => {
      setIsCheckingSaved(true);
      try {
        const saved = await isTradesPersonSavedByUser(user.id, trade.id);
        setIsSaved(saved);
      } catch (error) {
        debug.error('Error checking if tradesperson is saved:', error);
      } finally {
        setIsCheckingSaved(false);
      }
    };

    checkIfSaved();
  }, [user, trade.id]);

  // Focus textarea when editing notes
  useEffect(() => {
    if (isEditingNotes && notesRef.current) {
      notesRef.current.focus();
    }
  }, [isEditingNotes]);

  // Update notes state when prop changes
  useEffect(() => {
    setNotes(initialNotes || '');
  }, [initialNotes]);

  // Handle phone call
  const handleCall = () => {
    hapticPatterns.medium();
    if (trade.phone) {
      window.open(`tel:${trade.phone}`);
    } else {
      toast.info("No phone number available");
    }
  };

  // Generate message using our template system
  const generateTradeMessage = (): string => {
    try {
      // Use provided postcode, then profile postcode, then placeholder
      const rawPostcode = postcode || userProfile?.postcode;
      const userPostcode = rawPostcode ? formatPostcodeDisplay(rawPostcode) : '[POSTCODE]';
      const userName = userProfile?.username || userProfile?.email?.split('@')[0] || 'Your name';

      debug.log('TradePersonCard message generation:', {
        providedPostcode: postcode,
        profilePostcode: userProfile?.postcode,
        finalPostcode: userPostcode,
        userName,
        tradeType: tradeTypeDisplay.toLowerCase()
      });

      const templateData: QuickContactTemplateData = {
        userName,
        postcode: userPostcode,
        tradeType: tradeTypeDisplay.toLowerCase() || 'tradesperson',
        isFromSavedList: !!user && isSaved
      };

      const { body } = generateMessage('quickContact', templateData);
      return body;
    } catch (error) {
      debug.error('Error generating message from template:', error);
      // Fallback message
      return `Hi there, I'm looking for a ${tradeTypeDisplay || 'tradesperson'} near ${postcode ? formatPostcodeDisplay(postcode) : '[POSTCODE]'} and my neighbours recommended you. Are you available to come and look at a job soon?`;
    }
  };

  // Handle SMS messaging
  const handleMessage = () => {
    if (trade.phone) {
      const message = generateTradeMessage();
      window.open(`sms:${trade.phone}?body=${encodeURIComponent(message)}`);
    } else {
      toast.info("No phone number available for SMS");
    }
  };

  // Handle WhatsApp messaging
  const handleWhatsApp = () => {
    hapticPatterns.medium();
    if (trade.phone) {
      const message = generateTradeMessage();
      const whatsappUrl = generateWhatsAppUrl(trade.phone, message);

      // Use location.href for better mobile experience - avoids keeping browser tab open
      if (window.navigator.userAgent.includes('Mobile') || window.navigator.userAgent.includes('Android') || window.navigator.userAgent.includes('iPhone')) {
        window.location.href = whatsappUrl;
      } else {
        // On desktop, open in new tab and try to close it after a short delay
        const newWindow = window.open(whatsappUrl, '_blank');
        if (newWindow) {
          setTimeout(() => {
            try {
              newWindow.close();
            } catch (e) {
              // Ignore errors if we can't close the window
            }
          }, 1000);
        }
      }
    } else {
      toast.info("No phone number available for WhatsApp");
    }
  };

  // Handle email
  const handleEmail = () => {
    if (trade.email) {
      try {
        // Generate subject from template
        const templateData: QuickContactTemplateData = {
          userName: userProfile?.username || userProfile?.email?.split('@')[0] || 'Your name',
          postcode: postcode || userProfile?.postcode || '[POSTCODE]',
          tradeType: tradeTypeDisplay.toLowerCase() || 'tradesperson',
          isFromSavedList: !!user && isSaved
        };

        const { subject: templateSubject, body: templateBody } = generateMessage('quickContact', templateData);

        const subject = encodeURIComponent(templateSubject);
        const body = encodeURIComponent(templateBody);

        window.open(`mailto:${trade.email}?subject=${subject}&body=${body}`);
      } catch (error) {
        debug.error('Error generating email template:', error);
        // Fallback email
        const fallbackSubject = encodeURIComponent(`Looking for ${tradeTypeDisplay || 'tradesperson'}`);
        const fallbackBody = encodeURIComponent(generateTradeMessage());
        window.open(`mailto:${trade.email}?subject=${fallbackSubject}&body=${fallbackBody}`);
      }
    } else {
      toast.info("No email address available");
    }
  };

  // Handle copy contact info
  const handleCopyContact = () => {
    hapticPatterns.light();
    const contactText = `${displayName}${trade.phone ? ` - ${trade.phone}` : ''}${trade.email ? ` - ${trade.email}` : ''}`;

    navigator.clipboard.writeText(contactText)
      .then(() => toast.success("Contact info copied to clipboard"))
      .catch(() => toast.error("Failed to copy contact info"));
  };

  // Handle share
  const handleShare = async () => {
    if (!username && mode === 'saved') {
      toast.info("Set up a username in settings to share tradespeople");
      return;
    }

    // Generate the share URL
    const baseUrl = window.location.origin;

    // Create URL with path parameter for highlighting specific tradesperson
    const shareUrl = `${baseUrl}/${username}/tradesperson/${trade.id}`;

    // Check if Web Share API is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${displayName} - ${tradeTypeDisplay}`,
          text: `Here's a ${tradeTypeDisplay} I recommend: ${displayName}`,
          url: shareUrl,
        });
      } catch (err) {
        // Fallback to clipboard if share fails or is cancelled
        handleCopyShareUrl(shareUrl);
      }
    } else {
      // Fallback for browsers without Web Share API
      handleCopyShareUrl(shareUrl);
    }
  };

  // Helper to copy share URL to clipboard
  const handleCopyShareUrl = (url: string) => {
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Share link copied to clipboard"))
      .catch(() => toast.error("Failed to copy share link"));
  };

  // Handle save tradesperson button click
  const handleSaveClick = async () => {
    if (!user) {
      toast.info("Sign in to save to TradesPurple");
      return;
    }

    if (isSaved) {
      toast.info("Already saved to your TradesPurple");
      return;
    }

    setIsSaving(true);
    try {
      await addUserTradesperson(user.id, trade.id, {
        notes: "",
        preferred: false
      });

      setIsSaved(true);
      toast.success("Added to your TradesPurple");

      // Call onSave callback if provided (for parent component refresh)
      if (onSave) {
        onSave();
      }
    } catch (error) {
      debug.error('Error saving tradesperson:', error);
      toast.error("Failed to add tradesperson");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle saving notes
  const handleSaveNotes = async () => {
    if (!relationId) {
      toast.error("Cannot save notes: missing relationship ID");
      return;
    }

    try {
      await updateUserTradesperson(relationId, { notes });
      toast.success("Notes saved");
      setIsEditingNotes(false);
    } catch (error) {
      debug.error('Error saving notes:', error);
      toast.error("Failed to save notes");
    }
  };

  // Handle cancel notes editing
  const handleCancelNotes = () => {
    setNotes(initialNotes || '');
    setIsEditingNotes(false);
  };

  // If removed, don't render anything
  if (isRemoved) {
    return null;
  }

  // Helper to truncate notes - show more content
  const truncateNotes = (text: string, maxLength = 120) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;

    // Try to break at a natural point (period, line break, etc.)
    const breakPoints = ['. ', '! ', '? ', '\n'];
    for (const point of breakPoints) {
      const pos = text.lastIndexOf(point, maxLength);
      if (pos > maxLength/2) {
        return `${text.substring(0, pos+1).trim()}...`;
      }
    }

    // Fallback to breaking at word boundary
    return `${text.substring(0, maxLength).trim()}...`;
  };

  return (
    <div className="px-4">
      <div className={`p-3 border-2 rounded-xl relative touch-hold ${
        confirmingDelete
          ? 'border-red-200 bg-gray-50/80 opacity-90'
          : 'border-purple-300 bg-white hover:bg-purple-50'
      } transition-all`}>

      {/* Delete Confirmation Overlay */}
      {confirmingDelete && (
        <div className="absolute inset-0 bg-white/95 rounded-xl flex items-center justify-center z-10 overflow-hidden">
          <div className="px-3 py-4 max-w-full text-center">
            <Trash2 className="mx-auto h-8 w-8 text-red-500 mb-2" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">Remove {displayName || 'tradesperson'}?</h3>
            <p className="text-sm text-gray-600 mb-3">This will remove them from your saved tradespeople list.</p>

            <div className="flex space-x-2 justify-center">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmingDelete(false)}
                className="px-4 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg h-9"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (onRemove) {
                    try {
                      // First set the card as removed in the UI for immediate feedback
                      setIsRemoved(true);

                      // Then call the parent component's removal function
                      onRemove(trade.id);

                      // Show success toast with undo option
                      toast.success(`Removed ${displayName || 'tradesperson'} from your list`, {
                        action: {
                          label: "Undo",
                          onClick: () => {
                            if (onUndoRemove) {
                              // If undo is clicked, reset our local state
                              setIsRemoved(false);
                              setConfirmingDelete(false);
                              // And call the parent's undo function
                              onUndoRemove(trade.id);
                            }
                          }
                        }
                      });
                    } catch (error) {
                      debug.error('Error removing tradesperson:', error);
                      toast.error(`Failed to remove ${displayName || 'tradesperson'}`);
                      // Reset states on error
                      setIsRemoved(false);
                      setConfirmingDelete(false);
                    }
                  }
                }}
                className="px-4 bg-red-600 hover:bg-red-700 rounded-lg h-9"
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Card Content - Compact Design - Clickable except for notes */}
      <div
        className="flex flex-col relative cursor-pointer"
        onClick={() => onToggleExpanded?.(trade.id)}
      >
        {/* Badge Container - Absolute Positioned */}
        <div className="absolute top-0 right-0 z-10 flex flex-col items-end gap-1">
          {/* Social Proof Badge */}
          {trade.badge_info && trade.badge_info.tier && (
            <>
              {trade.badge_info.tier === 'popular' && (
                <Badge variant="popular" className="border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in">
                  <Trophy className="h-2.5 w-2.5 mr-1" /> Popular
                </Badge>
              )}
              {trade.badge_info.tier === 'neighbor-count' && trade.badge_info.count && (
                <Badge variant="neighborCount" className="border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in">
                  <Users className="h-2.5 w-2.5 mr-1" /> <span className="animate-number-change">{trade.badge_info.count}</span>&nbsp;lists
                </Badge>
              )}
              {trade.badge_info.tier === 'new' && (
                <Badge variant="new" className="border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in">
                  <Sparkles className="h-2.5 w-2.5 mr-1" /> New
                </Badge>
              )}
            </>
          )}

          {/* Verified Badge - Shows below social proof badge if it exists */}
          {trade.verified && trade.badge_info?.tier && (
            mode === 'saved' ? (
              <div className="bg-green-100 rounded-full p-1 animate-fade-scale-in" style={{ animationDelay: '100ms' }}>
                <Check className="h-3 w-3 text-green-700 stroke-[3]" />
              </div>
            ) : (
              <Badge variant="verified" className="border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in" style={{ animationDelay: '100ms' }}>
                <Check className="h-2.5 w-2.5 mr-1 stroke-[3]" /> Verified
              </Badge>
            )
          )}
        </div>

        {/* Top Row: Name and Profession */}
        <div className="flex items-start mb-1 pr-24"> {/* Added more right padding to make space for social proof badges */}
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              <TradeIcon tradesperson={trade} className="h-16 w-16" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-purple-900 leading-tight">
                {displayName}
              </h3>
              <div className="flex items-center mt-0.5">
                <p className="text-base text-purple-600">{tradeTypeDisplay}</p>
                <div className="flex items-center ml-2 space-x-1">
                  {trade.phone && (
                    <Phone className="h-3 w-3 text-gray-400" />
                  )}
                  {trade.phone && (
                    <MessageSquare className="h-3 w-3 text-gray-400" />
                  )}
                  {trade.email && (
                    <Mail className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Verified Badge - Shows in badge container if no social proof badge */}
        {trade.verified && !trade.badge_info?.tier && (
          <div className="absolute top-0 right-0 z-10">
            {mode === 'saved' ? (
              <div className="bg-green-100 rounded-full p-1 animate-fade-scale-in">
                <Check className="h-3 w-3 text-green-700 stroke-[3]" />
              </div>
            ) : (
              <Badge className="bg-green-100 text-green-700 border-0 rounded-full px-1.5 py-0.5 text-xs font-medium animate-fade-scale-in">
                <Check className="h-2.5 w-2.5 mr-1 stroke-[3]" /> Verified
              </Badge>
            )}
          </div>
        )}

        {/* Notes Section - Only shown in saved mode */}
        {mode === 'saved' && notes && !isExpanded && (
          <div className="mb-1 px-3">
            <p className="text-sm text-gray-600 truncate">{notes}</p>
          </div>
        )}

        {/* Full notes section when expanded */}
        {mode === 'saved' && isExpanded && (
          <div
            className={`mb-2 px-3 py-2 rounded-md cursor-pointer ${isEditingNotes ? 'bg-gray-50 border border-gray-200' : 'bg-purple-50/40 hover:bg-purple-50/70'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isEditingNotes) setIsEditingNotes(true);
            }}
          >
            {isEditingNotes ? (
              <>
                <Textarea
                  ref={notesRef}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[100px] text-base border-gray-300 resize-none"
                  placeholder="Add notes about this tradesperson..."
                />
                <div className="flex justify-end space-x-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelNotes();
                    }}
                    className="h-8"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveNotes();
                    }}
                    className="h-8 bg-purple-700 hover:bg-purple-800"
                  >
                    <Save className="h-3.5 w-3.5 mr-1" /> Save
                  </Button>
                </div>
              </>
            ) : notes ? (
              <div className="text-sm text-gray-700 leading-relaxed">
                {notes}
              </div>
            ) : (
              <div className="text-sm text-gray-400 flex items-center py-1">
                <Edit className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                <span>Add notes...</span>
              </div>
            )}
          </div>
        )}

        {/* Save button for public mode */}
        {mode === 'public' && (
          <div className="mt-1">
            <Button
              size="sm"
              variant="outline"
              className={`flex items-center px-4 h-9 rounded-lg transition-all w-full
                ${isSaved
                  ? 'bg-green-100 hover:bg-green-100 text-green-700 border-green-200 animate-spring-bounce'
                  : 'bg-white hover:bg-purple-50 text-purple-700 border-purple-300'}`}
              onClick={(e) => {
                e.stopPropagation();
                handleSaveClick();
              }}
              disabled={isSaving || isCheckingSaved || isSaved}
              aria-label={isSaved ? "Saved" : "Save"}
            >
              {isSaved ? (
                <div className="flex items-center">
                  <Check className="h-4 w-4 mr-1.5 animate-fade-scale-in" />
                  <span className="font-medium text-sm">Saved</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <BookmarkPlus className="h-4 w-4 mr-1.5" />
                  <span className="font-medium text-sm">Save</span>
                </div>
              )}
            </Button>
          </div>
        )}


        {/* Expandable Action Buttons */}
        {isExpanded && (
          <div className="mt-3 animate-fade-scale-in">
            {/* Primary action buttons row */}
            <div className="flex justify-between items-center px-2">
              {/* Left side: Main contact actions */}
              <div className="flex space-x-3">
                {trade.phone && (
                  <>
                    <ContactActionButton actionType="phone" label="Call" FallbackIcon={Phone} onClick={handleCall} />
                    <ContactActionButton actionType="sms" label="Message" FallbackIcon={MessageSquare} onClick={handleMessage} />
                    {isMobile && (
                      <ContactActionButton actionType="whatsapp" label="WhatsApp" FallbackIcon={MessageSquareShare} onClick={handleWhatsApp} />
                    )}
                  </>
                )}
                {trade.email && (
                  <ContactActionButton actionType="email" label="Email" FallbackIcon={Mail} onClick={handleEmail} />
                )}
              </div>

              {/* Right side: More options */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="p-3 hover:bg-purple-50 text-purple-700 transition-all active:scale-95 rounded-lg"
                    aria-label="More options"
                  >
                    <MoreVertical className="h-10 w-10" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-[180px] rounded-xl overflow-hidden shadow-lg">
                  <DropdownMenuItem onClick={handleCopyContact} className="py-2.5">
                    <Copy className="mr-2 h-4 w-4" />
                    <span>Copy Contact</span>
                  </DropdownMenuItem>

                  {/* Share and edit options for saved mode */}
                  {mode === 'saved' && (
                    <>
                      <DropdownMenuItem onClick={handleShare} className="py-2.5">
                        <Share2 className="mr-2 h-4 w-4" />
                        <span>Share Contact</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem onClick={() => setIsEditingNotes(true)} className="py-2.5">
                        <Edit className="mr-2 h-4 w-4" />
                        <span>Edit Notes</span>
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* Remove option */}
                  {mode === 'saved' && onRemove && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setConfirmingDelete(true)}
                        className="py-2.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Remove</span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        {/* Recommendation count - only shown if available */}
        {trade.recommendation_count !== undefined && trade.recommendation_count > 0 && (
          <div className="flex items-center text-xs text-purple-700 mt-1">
            <Check className="h-3 w-3 mr-1 text-purple-700" />
            <span>
              {trade.recommendation_count} {trade.recommendation_count === 1 ? 'recommendation' : 'recommendations'}
            </span>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
